/**
 * TransactionRow — pojedynczy wiersz na osi czasu.
 * Layout: ikona kategorii w kolorowym kółku, nazwa transakcji + meta, kwota tabular.
 * Tap (Faza 4) → otwiera modal edycji.
 */
import { Icons } from '../Icons';
import { getCategoryIcon } from '../../lib/categoryIcons';
import { formatCurrency, safeColor } from '../../lib/utils';
import type { Transaction } from '../../features/transactions/transactionsStore';
import type { Category } from '../../features/categories/categoriesStore';

interface Props {
  tx: Transaction;
  category?: Category;
  walletNamesById: Record<string, string>;
  onClick?: (tx: Transaction) => void;
}

export function TransactionRow({ tx, category, walletNamesById, onClick }: Props) {
  const isTransfer = tx.type === 'transfer';
  const isIncome = tx.type === 'income';

  const Icon = isTransfer ? Icons.Transfer : getCategoryIcon(category?.icon);
  const color = isTransfer ? '#0EA5E9' : safeColor(category?.color ?? '#94A3B8');

  const amountColor = isIncome
    ? 'text-emerald-600 dark:text-emerald-300'
    : isTransfer
    ? 'text-sky-600 dark:text-sky-300'
    : 'text-slate-900 dark:text-white';
  const amountPrefix = isIncome ? '+' : isTransfer ? '' : '-';

  const subtitle = isTransfer
    ? `${walletNamesById[tx.walletId] ?? '?'} → ${walletNamesById[tx.toWalletId ?? ''] ?? '?'}`
    : category?.name ?? 'Inne';

  return (
    <button
      type="button"
      onClick={onClick ? () => onClick(tx) : undefined}
      className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors text-left"
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${color}20`, color }}
      >
        <Icon size={20} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{tx.note || subtitle}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {subtitle}
          {tx.isRecurring && (
            <span className="ml-1.5 inline-flex items-center gap-0.5 text-sky-600 dark:text-sky-400">
              <Icons.Refresh size={10} /> cykl.
            </span>
          )}
        </p>
      </div>
      <p className={`text-sm font-bold tabular-nums shrink-0 ${amountColor}`}>
        {amountPrefix}
        {formatCurrency(Math.abs(tx.amount))}
      </p>
    </button>
  );
}
